<template>
  <div class="orders-container">
    <div v-if="state.orderId && selectedOrder" class="order-back pointer flex-inline ul" @click="resetOrderId()">
      <ArrowDropLeftLine />Back to All Orders
    </div>
    <div v-if="loading" class="flex cntr">
      <Loader />
    </div>
    <div v-else-if="state.orderId && selectedOrder" class="order-details">
      <div class="main-content">
        <div class="h2 contents">Order Contents</div>
        <div v-if="state.orderShippingDetails" class="shipping-full h4">
          Items shipped to
          {{
            state.orderShippingDetails.street_1 +
            (state.orderShippingDetails.street_2 ? ' ' + state.orderShippingDetails.street_2 : '')
          }}, {{ state.orderShippingDetails.city }}, {{ state.orderShippingDetails.state }}
          {{ state.orderShippingDetails.zip }}, {{ state.orderShippingDetails.country }}
        </div>
        <div v-for="product in state.orderProducts" :key="product.id" class="product-row">
          <div class="flex between">
            <div class="h4">{{ product.quantity }} x {{ product.name }}</div>
            <div class="h4 price">
              {{ currencySymbol + formatPrice(product.price_ex_tax) }}
            </div>
          </div>
          <div v-if="product?.product_options?.length" class="variation-section flex-col mgn-top-pt5">
            <span
              v-for="option in product.product_options"
              :key="option.nameId + option.value"
              class="variation-detail"
            >
              {{ option.display_name + ': ' + option.display_value }}
            </span>
          </div>
        </div>
        <div class="flex">
          <ul>
            <li>
              <span>{{ useTranslate('CartSlideout.subtotal') }}: </span
              >{{ currencySymbol + formatPrice(selectedOrder.subtotal_ex_tax) }}
            </li>
            <li><span>Sales Tax: </span>{{ currencySymbol + formatPrice(selectedOrder.total_tax) }}</li>
            <li v-if="parseFloat(selectedOrder.store_credit_amount)">
              <span>Store Credit: </span>{{ '-' + currencySymbol + formatPrice(selectedOrder.store_credit_amount) }}
            </li>
            <li v-if="parseFloat(selectedOrder.discount_amount)">
              <span>Discounted Amount: </span>{{ '-' + currencySymbol + formatPrice(selectedOrder.discount_amount) }}
            </li>
            <li>
              <span>Grand {{ useTranslate('CartSlideout.total') }}: </span
              ><span class="grand-total">{{ currencySymbol + formatPrice(selectedOrder.total_inc_tax) }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="side-content">
        <section v-if="selectedOrder">
          <div class="h3">Order Details</div>
          <div>Order Status: {{ selectedOrder.status }}</div>
          <div>Order Date: {{ convertDate(selectedOrder.date_created) }}</div>
          <div>
            Order {{ useTranslate('CartSlideout.total') }}:
            {{ currencySymbol + formatPrice(selectedOrder.total_inc_tax) }}
          </div>
          <div>Payment Method: {{ selectedOrder.payment_method }}</div>
          <div>
            Payment Status: <span class="uc">{{ selectedOrder.payment_status }}</span>
          </div>
        </section>
        <section v-if="state.orderShipmentDetails">
          <div class="h3">Ship To</div>
          <div>{{ state.orderShippingDetails.first_name + ' ' + state.orderShippingDetails.last_name }}</div>
          <div>{{ state.orderShippingDetails.street_1 }}</div>
          <div v-if="state.orderShippingDetails.street_2">
            {{ state.orderShippingDetails.street_2 }}
          </div>
          <div>
            {{ state.orderShippingDetails.city }}, {{ state.orderShippingDetails.state }}
            {{ state.orderShippingDetails.zip }}
          </div>
          <div>{{ state.orderShippingDetails.country }}</div>
        </section>
        <section v-if="selectedOrder">
          <div class="h3">Bill To</div>
          <div>{{ selectedOrder.billing_address.first_name + ' ' + selectedOrder.billing_address.last_name }}</div>
          <div>
            {{ selectedOrder.billing_address.street_1 }}
            {{ selectedOrder.billing_address.street_2 ? selectedOrder.billing_address.street_2 : '' }}
          </div>
          <div>
            {{ selectedOrder.billing_address.city }}, {{ selectedOrder.billing_address.state }}
            {{ selectedOrder.billing_address.zip }}
          </div>
          <div>{{ selectedOrder.billing_address.country }}</div>
        </section>
        <section v-if="state.orderShipmentDetails">
          <div class="h3">Shipping Details</div>
          <template v-for="shipment in state.orderShipmentDetails" :key="shipment.id">
            <div class="shipment">
              <div>Date Shipped: {{ convertDate(shipment.date_created) }}</div>
              <div>
                Shipping Method: <span class="uc">{{ shipment.shipping_provider }}</span>
              </div>
              <div>
                Tracking Link:
                <a :href="getShipLink(shipment)" target="_blank" class="tracking-link ul"
                  >{{ shipment.tracking_number }} - {{ shipment.shipping_method }}</a
                >
              </div>
            </div>
          </template>
        </section>
      </div>
    </div>
    <ul v-else-if="orders?.length">
      <li
        v-for="order in orders"
        :key="order.id"
        class="order pointer between desk-hor-mob-col"
        aria-label="View Order Details"
        @click="viewDetails(order.id)"
      >
        <!-- <div>img</div> -->
        <div class="order-data flex-col">
          <div class="order-id">Order #{{ order.id }}</div>
          <div class="order-price">
            {{ order.items_total }} product{{ order.items_total > 1 ? 's' : '' }} totaling
            {{ currencySymbol + formatPrice(order.total_inc_tax) }}
          </div>
          <div class="order-dates between desk-hor-mob-col">
            <div class="order-placed flex-col">
              {{ useTranslate('AccountPage.orderPlaced') }}
              <div class="date">
                {{ convertDate(order.date_created) }}
              </div>
            </div>
            <div class="order-update flex-col">
              {{ useTranslate('AccountPage.lastUpdate') }}
              <div class="date">
                {{ convertDate(order.date_modified) }}
              </div>
            </div>
          </div>
        </div>
        <div class="order-status">
          {{ order.status }}
        </div>
        <!-- <div v-if="order.staff_notes">
          {{ order.staff_notes }}
        </div> -->
      </li>
    </ul>
    <div v-else class="flex cntr">
      <h3>No Orders for this Account</h3>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from 'lodash-es';
import { getFirstIndex } from '~/util/arrays';
import { getCookie, CookieKeys } from '~/util/storage';
import { useCustomerStore } from '~/store/customer';
import { useLocaleStore } from '~/store/locale';

const { regionHeaderName, regionLocaleProperties } = useRegionRequestSettings();

const route = useRoute();
const customerStore = useCustomerStore();
const localeStore = useLocaleStore();

const state = reactive({
  orderId: (route as any)?.query?.orderId as number | undefined, // type string if retrieved from url params on init load
  orderProducts: null as any | null,
  orderShippingDetails: null as any | null,
  orderShipmentDetails: null as any | null,
  orderShippingQuotes: null as any | null,
});

const loading = computed(() => customerStore.loading);
const orders = computed(() => customerStore.orders);
const currencySymbol = computed(() => localeStore.currencySymbol);
const selectedOrder = computed<any | null>(() => {
  if (!isEmpty(orders) && state.orderId) {
    const ordersToArray = Object.values(orders.value);
    const orderIdTyped = typeof state.orderId === 'string' ? parseFloat(state.orderId) : state.orderId;
    return ordersToArray.find((order: any) => order.id === orderIdTyped);
  }
  return null;
});

// watch: {
//   orders(updatedOrders)
// },
onMounted(async () => {
  if (state.orderId) {
    await viewDetails(state.orderId);
  }
});

async function viewDetails(id: number): Promise<void> {
  const responses = await Promise.all([getOrderProducts(id), getOrderShipping(id), getOrderShipmentDetails(id)]);
  state.orderProducts = responses?.[0];
  state.orderShippingDetails = getFirstIndex(responses?.[1] as any[]);
  state.orderShipmentDetails = responses?.[2];

  state.orderShippingQuotes = await getOrderShippingQuotes(id, state.orderShippingDetails?.id); // orderShippingDetails is required before this
  state.orderId = id;
  if (typeof window !== 'undefined') {
    const currentUrl = window.location.href;
    const urlObj = new URL(currentUrl);
    urlObj.searchParams.set('orderId', id.toString());
    window.history.pushState(null, '', urlObj.toString());
  }
}
async function getOrderProducts(id: number): Promise<{ [index: string]: unknown } | unknown> {
  try {
    const soloUserCookie = CookieKeys.User;
    const jwt = getCookie(soloUserCookie);
    const body = JSON.stringify({
      jwt,
      orderId: id,
    });
    const res = await useFetchApiLayer('/getOrderProducts', {
      method: 'POST',
      headers: {
        [regionHeaderName]: regionLocaleProperties.value.regionCode,
      },
      body,
    });
    return res;
  } catch (error) {
    return Promise.resolve(error);
  }
}
async function getOrderShipping(orderId: number) {
  try {
    const body = JSON.stringify({
      orderId,
    });
    const res = await useFetchApiLayer('/getOrderShippingDetails', {
      method: 'POST',
      headers: {
        [regionHeaderName]: regionLocaleProperties.value.regionCode,
      },
      body,
    });
    return res;
  } catch (error) {
    return Promise.resolve(error);
  }
}
async function getOrderShipmentDetails(orderId: number) {
  try {
    const body = JSON.stringify({
      orderId,
    });
    const res = await useFetchApiLayer('/getOrderShipmentDetails', {
      method: 'POST',
      headers: {
        [regionHeaderName]: regionLocaleProperties.value.regionCode,
      },
      body,
    });
    return res;
  } catch (error) {
    return Promise.resolve(error);
  }
}
async function getOrderShippingQuotes(orderId: number, shippingId: number) {
  try {
    const body = JSON.stringify({
      orderId,
      shippingId,
    });
    const res = await useFetchApiLayer('/getOrderShippingQuotes', {
      method: 'POST',
      headers: {
        [regionHeaderName]: regionLocaleProperties.value.regionCode,
      },
      body,
    });
    return res;
  } catch (error) {
    return Promise.resolve(error);
  }
}
function getShipLink(shipment: any) {
  if (shipment.shipping_provider === 'fedex')
    return shipment?.generated_tracking_link || `https://www.fedex.com/fedextrack/?trknbr=${shipment?.tracking_number}`;
  return shipment?.generated_tracking_link || shipment?.tracking_link || shipment.comments;
}
function resetOrderId() {
  state.orderId = undefined;
  if (typeof window !== 'undefined') {
    const currentUrl = window.location.href;
    const urlObj = new URL(currentUrl);
    urlObj.searchParams.delete('orderId');
    window.history.pushState(null, '', urlObj.toString());
  }
}
function convertDate(date: string) {
  const dateObj = new Date(date);
  return dateObj.toDateString();
}
function formatPrice(price: string) {
  return parseFloat(price).toFixed(2);
}
</script>
<style lang="scss" scoped>
.order {
  &:hover {
    .order-id {
      text-decoration: underline;
    }
  }
}
.orders-container {
  margin-top: 3rem;
}
.order-back {
  color: $color-primary-600;
  :deep(svg) {
    fill: $color-primary-600;
  }
}
li {
  border-bottom: 1px solid $color-neutral-cool-200;
  padding: 1.333rem 0;
}
.order-id {
  font-weight: 500;
  color: $color-primary-600;
  font-size: #{local-functions.rem-calc(30)};
  white-space: nowrap;
}
.order-data {
  width: 30%;
  @include local-mixins.tablet_and_mobile {
    width: 100%;
    margin-bottom: 1rem;
  }
}
.order-price {
  margin-bottom: 1.333rem;
}
.order-placed,
.order-update {
  font-size: 0.75rem;
  font-weight: 500;
  .date {
    margin-top: 6px;
    font-weight: normal;
    font-size: 1.125rem;
  }
}
.order-update {
  @include local-mixins.tablet_and_mobile {
    margin-top: 1rem;
  }
}
.order-status {
  background-color: $color-neutral-cool-200;
  color: $color-neutral-white;
  height: fit-content;
  font-weight: 600;
  font-size: 0.75rem;
  padding: 0.16667rem 0.44444rem;
  @include local-mixins.tablet_and_mobile {
    width: fit-content;
  }
}
.order-details {
  display: flex;
  .contents {
    border-bottom: 1px solid $color-neutral-cool-200;
    margin-bottom: 1rem;
  }
  .main-content {
    width: 50%;
    .shipping-full {
      font-weight: 500;
      margin-top: 1.5rem;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid $color-neutral-cool-200;
    }
    .product-row {
      padding: 1rem 0;
      border-bottom: 1px solid $color-neutral-cool-200;
    }
    .variation-detail {
      font-size: 1rem;
    }
    ul {
      width: 100%;
      margin-top: 1rem;
    }
    li {
      width: 60%;
      margin-left: auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .grand-total {
        font-size: 1.5rem;
      }
    }
    .price {
      font-weight: 100;
    }
  }
  .side-content {
    width: 50%;
    .h3 {
      padding-bottom: 0.5rem;
      margin-bottom: 1rem;
      border-bottom: 1px solid $color-neutral-cool-200;
    }
    section {
      margin-bottom: 1.5rem;
      div {
        line-height: 1.5;
      }
    }
    .shipment {
      margin-bottom: 1.5rem;
      div {
        line-height: 1.5;
      }
    }
    .tracking-link {
      color: $color-primary-600;
    }
  }
  @include local-mixins.desktop {
    .main-content {
      margin-right: #{local-functions.rem-calc(60)};
    }
  }
  @include local-mixins.tablet_and_mobile {
    flex-direction: column;
    .main-content,
    .side-content {
      width: 100%;
    }
    .side-content {
      margin-top: 2rem;
    }
  }
}
</style>
